import React, { FC, useEffect } from "react";
// router (incl. auto-generated route tree)
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "../../routeTree.gen";
import { backendApi } from "src/backendApi/backendApi";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

const VERIFICATION_INTERVAL_SECONDS = 10;

export type RouterContext = {
  auth: { lastVerified: number | null; verifyAuth: () => Promise<boolean> };
};

const context: RouterContext = {
  auth: {
    lastVerified: null,
    async verifyAuth() {
      const verifiedRecently =
        this.lastVerified &&
        Date.now() - this.lastVerified < VERIFICATION_INTERVAL_SECONDS * 1000;

      if (verifiedRecently) {
        return true;
      }

      try {
        await backendApi.verifyJwtAuthOrLogout();
        this.lastVerified = Date.now();
      } catch (error) {
        return false;
      }
      return true;
    },
  },
};

// Create a new router instance
export const router = createRouter({
  routeTree,
  context,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  // eslint-disable-next-line no-unused-vars
  interface Register {
    router: typeof router;
  }
}

const TanStackRouterDevtools =
  process.env.NODE_ENV === "production"
    ? (): null => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import("@tanstack/react-router-devtools").then((res) => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        })),
      );

export const TanstackRouterSetup: FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    backendApi.onForcedLogout = () => {
      notifications.show({
        color: "orange",
        message: t("notifications.loggedOut"),
      });
    };
  });

  return (
    <>
      <RouterProvider router={router} />
      <React.Suspense>
        <TanStackRouterDevtools router={router} />
      </React.Suspense>
    </>
  );
};
